/* System defined core library */
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as $ from 'jquery';
import { Subscription } from 'rxjs';

/* User defined library/components */
import { BootstrapParams } from '../../../../../bootstrap-params';
import { ExamFeatureService } from '../../../../shared/services/exam-feature.service';
import { UtilityService } from '../../../../shared/services/utility.service';
import { SelectedExamDetailService } from '../../services/selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../../../../shared/models/scheduling/appointmentdetail.model';
import { BatteryConfig } from '../../../../shared/models/scheduling/batteryConfig.model';
import { Candidate } from '../../../../shared/models/scheduling/candidate.model';
import { Client } from '../../../../shared/models/scheduling/client.model';
import { Eligibility, EligibilityDetail } from '../../../../shared/models/scheduling/eligibility.model';
import { Exam } from '../../../../shared/models/scheduling/exam.model';
import { Program } from '../../../../shared/models/scheduling/program.model';
import { SiteSlot } from '../../../../shared/models/scheduling/siteSlot.model';
import { Slot } from '../../../../shared/models/scheduling/slot.model';
import { TestingAccommodation } from '../../../../shared/models/scheduling/TestingAccommodation.model';
import { Timezone } from '../../../../shared/models/scheduling/timezone.model';


@Component({
  selector: 'app-selected-exam-detail',
  templateUrl: './selected-exam-detail.component.html',
  styleUrls: ['./selected-exam-detail.component.scss'],
})
export class SelectedExamDetailComponent implements OnChanges, OnDestroy {

  /* User defined private/public variables  */
  public errorMessage: string = '';
  public client: Client;
  public program: Program;
  public exam: Exam;
  public siteSlot: SiteSlot;
  public slot: Slot;
  public eligibility: Eligibility;
  public eligibilityDetails: EligibilityDetail[] = [];
  public isnavigated: boolean = false;
  public appointmentCount: any;
  public testingAccommodation: TestingAccommodation[];
  public programLogo: any;
  public isProctor: boolean = false;
  public candidate: Candidate;
  public timezone: Timezone;
  public batteryConfig: BatteryConfig;
  public totalDuration: number = 0;

  /* Input-OutPut parameters decalartions */
  @Input() public appointmentDetails: AppointmentDetail[] = [];
  @Input() public selectedProgram: Program;

  /* Service subscriptions */
  client$: Subscription;
  program$: Subscription;
  region$: Subscription;
  exam$: Subscription;
  siteSlot$: Subscription;
  slot$: Subscription;
  selectedLocation$: Subscription;
  country$: Subscription;
  state$: Subscription;
  eligibility$: Subscription;
  eligibilityDetails$: Subscription;
  appointmentDetailData$: Subscription;
  appointmentCount$: Subscription;
  testingAccommodation$: Subscription;
  programLogo$: Subscription;
  translateLanguage$: Subscription;
  candidate$: Subscription;
  timezone$: Subscription;
  batteryConfig$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private bootStrapParams: BootstrapParams,
    private selectedExamDetailService: SelectedExamDetailService,
    private utilityService: UtilityService,
    private translate: TranslateService
  ) {

    this.client$ = selectedExamDetailService.client.subscribe(
      client => {
        this.client = client;
      });

    this.program$ = selectedExamDetailService.program.subscribe(
      program => {
        this.program = program;
      });

    this.batteryConfig$ = selectedExamDetailService.batteryConfig.subscribe(
      batteryConfig => {
        this.batteryConfig = batteryConfig;
      });

    this.appointmentDetailData$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      appointDetailData => {
        this.appointmentDetails = appointDetailData;
        if (appointDetailData) this.totalDuration = ExamFeatureService.calculateTotalDuration(this.appointmentDetails, this.batteryConfig);
      });

    this.exam$ = selectedExamDetailService.exam.subscribe(
      exam => {
        this.exam = exam;
      });

    this.siteSlot$ = selectedExamDetailService.siteSlot.subscribe(
      siteSlot => {
        this.siteSlot = siteSlot;
      });

    this.slot$ = selectedExamDetailService.slot.subscribe(
      slot => {
        this.slot = slot;
      });

    this.eligibility$ = selectedExamDetailService.eligibility.subscribe(
      eligibility => {
        this.eligibility = eligibility;
      });

    this.eligibilityDetails$ = selectedExamDetailService.eligibilityDetails.subscribe(
      eligibilityDetails => {
        this.eligibilityDetails = eligibilityDetails;
      });

    this.appointmentCount$ = this.selectedExamDetailService.appointmentCountForInfo.subscribe(
      (value: number) => {
        this.appointmentCount = value;
      });

    this.testingAccommodation$ = selectedExamDetailService.applicableTestingAccommodations.subscribe(
      applicableTestingAccommodations => {
        this.testingAccommodation = applicableTestingAccommodations;
      });

    this.programLogo$ = selectedExamDetailService.programLogo.subscribe(
      programLogo => {
        this.programLogo = programLogo;
      });

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.candidate$ = selectedExamDetailService.candidate.subscribe(
      candidate => {
        this.candidate = candidate;
      });

    this.timezone$ = selectedExamDetailService.timezone.subscribe(
      timezone => {
        this.timezone = timezone;
      });

    this.isProctor = this.selectedExamDetailService.isProctored;
  }

  /**
   * check if image exists with the input source url
   * using height and return true
   * @param imageUrl
   */
  checkIfImageExists(imageUrl: any) {
    const image = new Image();
    image.src = imageUrl;
    $(image).on('load', () => {

    });
    this.programLogo = image;
    this.selectedExamDetailService.setProgramLogo(this.programLogo);
    return !!image.height;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.selectedProgram && changes.selectedProgram.currentValue) {
      const currentChanges = changes.selectedProgram.currentValue;
      if (currentChanges && currentChanges.code) {
        this.checkIfImageExists('../../../../../../Assets/images/programs/' + currentChanges.code + '.gif');
      }
    }
  }

  shouldTranslate(ta: any) {
    if (ta && ta.displayId) {
      return (ta.displayId.toString().toLowerCase() === 'centerwithwheelchairaccess' ||
        ta.displayId.toString().toLowerCase() === 'examvoiceoverlanguage' ||
        ta.displayId.toString().toLowerCase() === 'signlanguagevideo');
    }
    return false;
  }

  durationToStr(val: number) {
    return this.utilityService.durationToStr(val);
  }

  getClass() {
    let clientCode = this.selectedExamDetailService.clientCode ? this.selectedExamDetailService.clientCode.toLowerCase() : '';
    let programCode = this.selectedExamDetailService.programCode ? this.selectedExamDetailService.programCode.toLowerCase() : '';
    let classes=`${clientCode} ${programCode}`;
    if (this.getName()) {classes+=' selected-code'}
    return classes;
  }

  getName() {
    if (
      this.candidate &&
      this.candidate.details
    ) {
      let fullname = '';
      if (!!this.candidate.details['FIRST_NAME']) { fullname += this.candidate.details['FIRST_NAME'] + ' '; }
      else if (!!this.candidate.details['FIRSTNAME_RSA']) { fullname += this.candidate.details['FIRSTNAME_RSA'] + ' '; }

      if (!!this.candidate.details['LAST_NAME']) { fullname += this.candidate.details['LAST_NAME']; }
      else if (!!this.candidate.details['LASTNAME_RSA']) { fullname += this.candidate.details['LASTNAME_RSA']; }
      return fullname;
    } else if (
      this.candidate &&
      this.candidate.defaultDetails
    ) {
      let fullname = '';
      if (!!this.candidate.defaultDetails['first_name']) { fullname += this.candidate.defaultDetails['first_name'] + ' '; }
      if (!!this.candidate.defaultDetails['last_name']) { fullname += this.candidate.defaultDetails['last_name']; }
      return fullname;
    }
  }

  /* Reset the shared service if componenet is unloaded */
  ngOnDestroy(): void {

    if (this.client != null) this.client.name = "";
    if (this.program != null) this.program.name = "";
    if (this.exam != null) this.exam.name = "";
    if (this.slot != null) this.slot = null;
    if (this.siteSlot != null) this.siteSlot.location = null;

    this.selectedExamDetailService.setCountry(null);
    this.selectedExamDetailService.setState(null);
    this.selectedExamDetailService.setSlot(null);
    this.selectedExamDetailService.setEligibility(null);
    this.selectedExamDetailService.setClient(null);
    this.selectedExamDetailService.setProgram(null);
    this.selectedExamDetailService.SetCandidateDataSource(null);
    this.selectedExamDetailService.setHold(null);
    this.selectedExamDetailService.setExam(null);
    this.selectedExamDetailService.setAppointmentSearch(null);
    this.selectedExamDetailService.postedConfirmationNumber = "";
    this.selectedExamDetailService.setRegion(null);
    this.selectedExamDetailService.setSelectedLocation(null);
    this.selectedExamDetailService.isBackToBackRequired = false;
    this.selectedExamDetailService.prevSelectedPopoverObject = null;
    this.selectedExamDetailService.setProgramLogo(null);
    this.selectedExamDetailService.setTimeZone(null);
  }
}
